/**
 * Registers a pay-card proxy id [KPC-1].
 *
 * **The id is a parameter and never state.** It identifies a financial instrument, so it is passed in, sent
 * once, and never stored anywhere this module can reach — not in the session, not in a module-level
 * variable, not in a retry buffer. `src/services/CLAUDE.md` forbids personal data in storage; this goes
 * further and keeps it out of memory beyond the call.
 */

import { kioskFetch } from './kioskFetch';
export type PayCardOutcome =
  | { readonly kind: 'registered' }
  /** The api refused the value — a malformed id. The candidate can correct it. */
  | { readonly kind: 'rejected' }
  /**
   * The request never produced an answer, OR the environment has writes disabled.
   *
   * Both route to the recruiter: a write that did not happen must never read as one that did, because a
   * candidate told they are registered when they are not finds out on payday.
   */
  | { readonly kind: 'unreachable' };

const REQUEST_TIMEOUT_MS = 10_000;

function apiBaseUrl(): string {
  return import.meta.env.VITE_API_BASE_URL ?? '';
}

export async function registerPayCard(
  candidateId: number,
  proxyId: string,
): Promise<PayCardOutcome> {
  let response: Response;
  try {
    response = await kioskFetch(`${apiBaseUrl()}/pay-card`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ candidateId, proxyId }),
      signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
    });
  } catch {
    return { kind: 'unreachable' };
  }

  if (response.ok) return { kind: 'registered' };
  // 400 is the candidate's to fix; everything else is ours, and both are the recruiter's fallback if the
  // candidate cannot get past it.
  return response.status === 400 ? { kind: 'rejected' } : { kind: 'unreachable' };
}
